import React from 'react';
import { useToastsContext } from '@organisms/Toasts/ToastsProvider';
import {
  extractErrorMessageFromException,
  extractFirstErrorCode,
  tryMapErrorMessage,
} from '@utils/errorHelpers';

const useSnackbar = () => {
  const { show } = useToastsContext();

  const displayInfo = React.useCallback((message: string) => {
    show(message, 'info');
  }, [show]);

  const displayError = React.useCallback((message: string) => {
    show(message, 'error');
  }, [show]);

  const displayWarning = React.useCallback((message: string) => {
    show(message, 'warning');
  }, [show]);

  const displaySuccess = React.useCallback((message: string) => {
    show(message, 'success');
  }, [show]);

  const displayErrorFromException = React.useCallback((err: unknown, defaultMessage: string, errorMap: Record<string, string>) => {
    const errorCode = extractFirstErrorCode(err);
    const errorMessage = tryMapErrorMessage(errorCode, defaultMessage, errorMap);
    show(errorMessage, 'error');
  }, [show]);

  const displayException = React.useCallback((defaultMessage: string) => {
    return (err: unknown) => {
      const errorMessage = extractErrorMessageFromException(err, defaultMessage);
      show(errorMessage, 'error');
    };
  }, [show]);

  return React.useMemo(
    () => ({ displayInfo, displaySuccess, displayWarning, displayError, displayErrorFromException, displayException }),
    [displayInfo, displaySuccess, displayWarning, displayError, displayErrorFromException, displayException]
  );
};

export default useSnackbar;
